import React from "react";
import { Flex } from "../../../../components/Flex";
import { FormsFolder as FormsFolderModel } from "../../../../model/form/types/FormsFolder";
import styled from "styled-components";
import { Spacing } from "../../../../theme/Spacing";
import { Shadow } from "../../../../theme/Shadow";
import { Rounded } from "../../../../theme/Rounded";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { faFolder } from "@fortawesome/free-solid-svg-icons/faFolder";
import { Text } from "../../../../components/Text";
import { useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { standardDate } from "../../../../utils/Date";
import { Button } from "../../../../components/Button";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { Tooltip } from "../../../../utils/local-antd";
import { faFileAlt } from "@fortawesome/free-solid-svg-icons/faFileAlt";
import { useTranslation } from "react-i18next";
import { useAtiraFormContext } from "../../context/form-context";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { useNavigate } from "react-router-dom";
import { getFontAwesomeIcon } from "../../../../utils/getFontAwesomeIcon";
import { TextWithTruncated } from "../../../../components/TextWithTruncated";
import { Lengths } from "../../../../model/shared/enum/Lengths.enum";
import { TestIDs } from "../../../../tests/types/testIds";

const Card = styled(Flex)`
  flex-direction: column;
  background-color: ${({ theme }) => theme.white};
  box-shadow: ${Shadow.SM};
  border-radius: ${Rounded.md};
`;

const Footer = styled(Flex)`
  background-color: ${({ theme }) => theme.main};
  flex-direction: column;
  padding: ${Spacing.s};
  border-radius: 0 0 ${Rounded.md} ${Rounded.md};
  flex-grow: 1;
`;

const IconButton = styled(Button)`
  padding: ${Spacing.xs};
  margin: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.sub};
`;

const FolderIconWrapper = styled(Flex)`
  padding: ${Spacing.l} ${Spacing.m};
  justify-content: center;
  cursor: pointer;
`;

type FormsFolderProps = {
  folder: FormsFolderModel;
};

export const FormsFolder: React.FC<FormsFolderProps> = ({ folder }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    setSelectedFolder,
    setFormsFolderUpdateVisible,
    setFormsFolderDeleteVisible,
  } = useAtiraFormContext();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const userPermissions = useAppSelector(userSliceSelectors.selectLoggedInUser)
    ?.permissions.permissions;

  const onEdit = () => {
    setSelectedFolder(folder);
    setFormsFolderUpdateVisible(true);
  };

  const onDelete = () => {
    setSelectedFolder(folder);
    setFormsFolderDeleteVisible(true);
  };

  const onViewForms = () => {
    navigate(
      `/forms/folder-${encodeURIComponent(folder.name.replace(/\s/g, "_"))}-${folder._id}`,
    );
  };

  return (
    <Card dataTestId={TestIDs.forms.folders.wrapper(folder._id)}>
      <FolderIconWrapper onClick={onViewForms}>
        <AtiraIcon
          icon={getFontAwesomeIcon(folder.icon) || faFolder}
          size="3x"
          color="main"
        />
      </FolderIconWrapper>

      <Footer>
        <Flex gap="xs">
          <TextWithTruncated
            fontWeight={"bold"}
            fontSize="s"
            color="white"
            length={15}
          >
            {folder.name}
          </TextWithTruncated>

          <Text fontWeight={"bold"} fontSize="s" color="white">
            ({folder?.formsIds?.length || 0})
          </Text>
        </Flex>

        {folder?.description ? (
          <TextWithTruncated length={Lengths.NAME} fontSize="s" color="sub">
            {folder.description}
          </TextWithTruncated>
        ) : null}

        <Flex
          justifyContent="space-between"
          gap="m"
          alignItems="center"
          marginTop="auto"
          paddingTop="s"
        >
          <Text fontSize="xs" color="sub">
            {standardDate<string>(folder.createdAt, user)}
          </Text>

          <Flex gap="xs">
            {userPermissions?.FORM.UPDATE === true ? (
              <Tooltip title={t("forms.folder.actions.edit")}>
                <IconButton
                  icon={faEdit}
                  onClick={onEdit}
                  dataTestId={TestIDs.forms.folders.edit(folder._id)}
                />
              </Tooltip>
            ) : null}

            {userPermissions?.FORM.READ === true ? (
              <Tooltip title={t("forms.folder.actions.viewForms")}>
                <IconButton icon={faFileAlt} onClick={onViewForms} />
              </Tooltip>
            ) : null}

            {userPermissions?.FORM.DELETE === true ? (
              <Tooltip title={t("common.delete")}>
                <IconButton
                  icon={faTrash}
                  onClick={onDelete}
                  dataTestId={TestIDs.forms.folders.delete.button(folder._id)}
                />
              </Tooltip>
            ) : null}
          </Flex>
        </Flex>
      </Footer>
    </Card>
  );
};
